/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Tue May 17 2023
 **/

import { ReactNode } from "react";
import { useTheme } from "@emotion/react";

import { useMouseEnterWait } from "@/frameworks/libraries/hooks";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";

import { ActiveStatus, Avatar, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import * as S from "./private/emotion";
import { fullWidthStyle, textEllipsisStyle, userItemButtonStyle } from "./private/emotion";

export default function UserItem({
	name,
	email,
	src = "",
	status,
	rank = "",
	isNew = false,
	textColor,
	isAdmin = false,
	isBlock = false,
	description = "",
	infoMaxWidth = "204px",
	hoverNameMaxWidth = "204px",
	hoverDescriptionMaxWidth = "204px",
	activeBackground,
	onAvatarClick,
	children,
	me,
	detail,
	isUserButtonStyle = true,
	typeUser,
	groupJoinRequest,
	...rest
}: {
	src?: string;
	name: string;
	email: ReactNode;
	status?: SwaggerModels.UserActiveStatus;
	rank?: string;
	isNew?: boolean;
	textColor?: Avatar.ProfileColor;
	isAdmin?: boolean;
	isBlock?: boolean;
	description?: string;
	infoMaxWidth?: UnitString;
	hoverNameMaxWidth?: UnitString;
	hoverDescriptionMaxWidth?: UnitString;
	onAvatarHover?: (e: MouseEvent) => void;
	children?: React.ReactNode;
	onAvatarClick?: (e: React.MouseEvent<HTMLElement>) => void;
	detail?: string;
	me?: boolean;
	activeBackground?: boolean;
	isUserButtonStyle?: boolean;
	typeUser?: "group" | "space" | "channel";
	groupJoinRequest?: boolean;
} & React.HTMLAttributes<HTMLElement>) {
	const theme = useTheme();
	const activeStatus = (type?: SwaggerModels.UserActiveStatus) => {
		switch (type) {
			case "ACTIVE":
				return "Green";
			case "OFFLINE":
				return "Gray";
			case "FOCUS":
				return "Red";
			case "AWAY":
				return "Yellow";
			default:
				return undefined;
		}
	};

	const { ref } = useMouseEnterWait<HTMLDivElement>((ev) => {
		rest.onAvatarHover?.(ev);
	});

	const getNameIcon = () => {
		switch (typeUser) {
			case "group":
				return "AdminMark24";
			case "space":
				return "AdminSpaceMark24";
			case "channel":
				return "AdminChannelMark24";
			default:
				return "AdminChannelMark24";
		}
	};

	return (
		<S.UserItem
			css={userItemButtonStyle(
				theme,
				infoMaxWidth,
				hoverDescriptionMaxWidth,
				hoverNameMaxWidth,
				activeBackground,
				isUserButtonStyle
			)}
			{...rest}
		>
			<div css={fullWidthStyle}>
				<S.UserInfoWrapper verticalAlign={isUserButtonStyle ? "center" : "top"}>
					<div ref={ref}>
						<Avatar
							kind="people_32"
							type="image"
							src={src}
							status={activeStatus(status) ?? "Gray"}
							text={(name ?? "").substring(0, 1)}
							textColor={textColor}
							onClick={onAvatarClick}
						/>
					</div>
					<S.InfoArea>
						<Row gap={4}>
							<ActiveStatus position="RightCenter" active={isNew}>
								<Typography.p
									className="user-name"
									typoSize="Body3_MD"
									css={textEllipsisStyle(infoMaxWidth)}
								>
									{groupJoinRequest ? email : `${name}${me ? ` ${_T("(나)")}` : ""}`}
								</Typography.p>
							</ActiveStatus>
							<Maybe test={isAdmin}>
								<S.IconWrapper verticalAlign="center" horizonAlign="center" type={typeUser}>
									<Icon name={getNameIcon()} size={14} />
								</S.IconWrapper>
							</Maybe>
							<Maybe test={isBlock}>
								<S.BlockIconWrapper verticalAlign="center" horizonAlign="center">
									<Icon name="Block24" size={14} />
								</S.BlockIconWrapper>
							</Maybe>
						</Row>
						<S.Description
							className="user-description"
							typoSize="Body5"
							css={textEllipsisStyle(infoMaxWidth)}
						>
							<Maybe test={!!description}>{description} • </Maybe>
							<Maybe test={!!rank}>{rank} • </Maybe>
							{detail || email}
						</S.Description>
					</S.InfoArea>
					{children}
				</S.UserInfoWrapper>
			</div>
		</S.UserItem>
	);
}
